/**
 * 브랜드 허브가 훑는 기간 — 프리셋(최근 N일) **또는** 직접 고른 두 날짜.
 *
 * 화면은 둘 중 하나만 쥐고, 조회하는 쪽은 **늘 같은 두 값**(`days` · `endMs`)을 받는다 —
 * 집계도 글 조회도 「끝 날짜에서 N일 거슬러 올라간다」는 한 규약으로 묻기 때문이다
 * (`toAnalyticsPeriod` · `toMediaOrganicScope` 둘 다 기준 시각을 받는다).
 *
 * ⚠️ `endMs` 가 **0 이면 「오늘까지」**다 — 프리셋에 `Date.now()` 를 박으면 매 렌더마다 값이
 * 달라져 react-query 열쇠가 어긋나고 같은 조회를 끝없이 다시 던진다.
 * 🚨 **플랜 밖의 기간은 조회에 닿기 전에 걷는다**(`sanitizeBrandHubPeriod`) — 세션에 남은 값은
 *   플랜이 내려간 뒤에도 그대로 되살아난다.
 */

import { periodLabelOf, PERIOD_PRESET_DAYS } from 'src/constants/plan-period';

import { CUSTOM_PERIOD_MAX_DAYS } from 'src/sections/trend-ranking/constants/trend-ranking-options';

/** 고른 것이 없거나 걷어 낼 때 돌아가는 프리셋 */
export const BRAND_HUB_DEFAULT_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** 직접 고른 두 날짜 — 비면 프리셋을 쓴다 */
export type BrandHubRange = { start: Date; end: Date } | null;

export type BrandHubPeriod = {
  /** 며칠치인가 — 직접 고르면 두 날짜 사이의 날 수(양끝 포함) */
  days: number;
  /** 끝 날짜의 밀리초 · 0 이면 오늘 */
  endMs: number;
  /** 화면에 적는 말 */
  label: string;
};

/** @param days 프리셋 일수 · @param range 직접 고른 두 날짜(있으면 이쪽이 이긴다) */
export function toBrandHubPeriod(days: number, range: BrandHubRange): BrandHubPeriod {
  if (!range) return { days, endMs: 0, label: periodLabelOf(days) };
  const [from, to] = range.start.getTime() <= range.end.getTime() ? [range.start, range.end] : [range.end, range.start];
  const span = Math.max(1, Math.round((startOfDay(to) - startOfDay(from)) / DAY_MS) + 1);
  return { days: span, endMs: to.getTime(), label: `${ymd(from)} ~ ${ymd(to)}` };
}

/**
 * 「직접 선택」이 열리나 — 최장 달력(`CUSTOM_PERIOD_MAX_DAYS`)까지 플랜이 열려 있어야 한다.
 * @param limitDays 플랜 상한(일) · 모르면 `null`(가르지 않는다)
 */
export function canPickBrandHubRange(limitDays: number | null): boolean {
  return limitDays === null || limitDays >= CUSTOM_PERIOD_MAX_DAYS;
}

/**
 * 달력이 거슬러 갈 수 있는 날 수 — 플랜 상한, 모르면 `CUSTOM_PERIOD_MAX_DAYS`.
 * @param limitDays 플랜 상한(일 · 이미 1년에서 접힌 값) · 모르면 `null`
 */
export function brandHubRangeLookback(limitDays: number | null): number {
  return limitDays ?? CUSTOM_PERIOD_MAX_DAYS;
}

/**
 * 일수를 **고를 수 있는 프리셋**으로 붙인다 — 프리셋에 없는 값(예: 60)이나 플랜 상한을 넘는 값이 세션에서 돌아온다.
 * 상한 이하 프리셋 중 가장 가까운 것이고, 거리가 같으면 짧은 쪽이다. 하나도 없으면 `BRAND_HUB_DEFAULT_DAYS`.
 * @param days 되살린 일수(아무 값)
 * @param limitDays 플랜 상한(일) · 모르면 `null`
 */
export function snapBrandHubDays(days: unknown, limitDays: number | null): number {
  const wanted = Number(days);
  if (!Number.isFinite(wanted) || wanted <= 0) return BRAND_HUB_DEFAULT_DAYS;
  const allowed = PERIOD_PRESET_DAYS.filter((preset) => limitDays === null || preset <= limitDays);
  if (allowed.length === 0) return BRAND_HUB_DEFAULT_DAYS;
  return allowed.reduce((best, preset) => (Math.abs(preset - wanted) < Math.abs(best - wanted) ? preset : best));
}

/**
 * 플랜이 허락하는 기간만 남긴다 — 세션에서 되살릴 때도, 플랜 값이 오거나 바뀔 때도 이것을 거친다.
 *
 * ```
 *   일수   → 상한 이하 프리셋으로 붙인다(snapBrandHubDays)
 *   달력   → 직접 선택이 안 열리거나 시작일이 거슬러 갈 수 있는 날보다 앞이면 버리고 30일 프리셋으로
 * ```
 * ⚠️ 달력을 그대로 두면 **같은 객체**를 돌려준다 — 부르는 쪽이 참조로 「바뀌었나」를 가른다.
 * @param input 화면이 쥔 일수·달력
 * @param limitDays 플랜 상한(일 · 이미 1년에서 접힌 값) · 모르면 `null`
 * @param nowMs 지금 시각(밀리초) — 달력 바닥을 오늘에서 센다
 */
export function sanitizeBrandHubPeriod(
  input: { days: unknown; range: BrandHubRange },
  limitDays: number | null,
  nowMs: number,
): { days: number; range: BrandHubRange } {
  const { range } = input;
  if (range && isRangeAllowed(range, limitDays, nowMs)) {
    return { days: snapBrandHubDays(input.days, limitDays), range };
  }
  const days = range ? snapBrandHubDays(BRAND_HUB_DEFAULT_DAYS, limitDays) : snapBrandHubDays(input.days, limitDays);
  return { days, range: null };
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 달력의 시작일이 「오늘 포함 최근 N일」 안인가 — 달력 고르개(`PeriodRangePicker`)의 바닥과 같은 셈 */
function isRangeAllowed(range: { start: Date; end: Date }, limitDays: number | null, nowMs: number): boolean {
  if (!canPickBrandHubRange(limitDays)) return false;
  const first = Math.min(range.start.getTime(), range.end.getTime());
  if (!Number.isFinite(first)) return false;
  const floor = startOfDay(new Date(nowMs)) - (brandHubRangeLookback(limitDays) - 1) * DAY_MS;
  return first >= floor;
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function ymd(date: Date): string {
  const y = String(date.getFullYear()).slice(2);
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}.${m}.${d}`;
}

/**
 * 기간을 **이벤트 캘린더 상류가 읽는 꼴**(`YYYY-MM-DD`)로 옮긴다.
 *
 * ⚠️ 상류는 **겹침**으로 읽는다(`from <= 검색끝 AND to >= 검색시작`) — 그 기간에 «걸쳐 있던»
 *   캠페인이 오지, 그 안에서 시작해 끝난 것만 오지 않는다.
 * ⚠️ `endMs` 가 0 이면 오늘까지다.
 */
export function toEventWindow(period: { days: number; endMs: number }): { from: string; to: string } {
  const end = period.endMs || Date.now();
  const start = end - (Math.max(1, period.days) - 1) * DAY_MS;
  return { from: isoDay(start), to: isoDay(end) };
}

function isoDay(ms: number): string {
  const date = new Date(ms);
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}
